// -----------------------------------------------------------------------------
// SendPaymentLinkPanel  (Wave C — minimal)
//
// Action panel body for SEND_PAYMENT_LINK.
// Minimal: amount input + channel picker + "Mark as sent" confirm.
// Full Stripe checkout-session creation is deferred — it requires Stripe config,
// preset amounts from practice preferences, and a multi-step flow.
// Marked as minimal in the Task #16 report.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    ToggleButtonGroup,
    ToggleButton,
    CircularProgress,
    Stack,
    InputAdornment,
} from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';
import InlineEmailAddField from 'src/components/InlineEmailAddField';

const CHANNELS = [
    { value: 'email',    label: 'Email',    icon: 'mdi:email-outline' },
    { value: 'sms',      label: 'SMS',      icon: 'mdi:message-text-outline' },
    { value: 'whatsapp', label: 'WhatsApp', icon: 'mdi:whatsapp' },
];

export default function SendPaymentLinkPanel({
    item,
    practiceId,
    leadId,
    leadDetail,
    onComplete,
    onClose,
}) {
    const [amount, setAmount] = useState('');
    const [channel, setChannel] = useState('email');
    const [submitting, setSubmitting] = useState(false);
    const [emailAdded, setEmailAdded] = useState('');

    const patientEmail = emailAdded || leadDetail?.email || leadDetail?.lead_contact?.email || '';
    const patientPhone = leadDetail?.lead_contact?.phone || '';

    const handleSend = async () => {
        if (!amount || Number(amount) <= 0) {
            dispatch(updateError({ success: false, message: 'Please enter a valid amount' }));
            return;
        }
        setSubmitting(true);
        try {
            const treatmentId = leadDetail?.lead_treatments?.[0]?.id || null;
            // Log as a note so there's an audit trail.
            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/notes/`,
                'post',
                {
                    lead: leadId,
                    // LeadNote API stores free text in `additional_notes` (`description` is ignored).
                    additional_notes: `Payment link sent via ${channel} for £${amount}`,
                    ...(treatmentId ? { treatment: treatmentId } : {}),
                }
            );
            if (res.status === 201) {
                dispatch(updateError({ success: true, message: `Payment link logged (£${amount} via ${channel})` }));
                onComplete?.(item);
            } else {
                dispatch(updateError({ success: false, message: 'Failed to record payment link' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Something went wrong' }));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            data-testid="send-payment-link-panel"
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                height: '100%',
                overflowY: 'auto',
            }}
        >
            {/* Icon header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(91, 181, 245, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon="mdi:credit-card-outline" width={18} height={18} sx={{ color: '#5BB5F5' }} />
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em' }}>
                    Send payment link
                </Typography>
            </Box>

            {/* Amount */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    Amount
                </Typography>
                <TextField
                    data-testid="payment-link-amount"
                    type="number"
                    size="small"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start">£</InputAdornment>,
                        inputProps: { min: 0, step: '0.01' },
                    }}
                    sx={{ width: 160 }}
                />
            </Box>

            {/* Channel */}
            <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#3c3c43', letterSpacing: '-0.005em', mb: 0.5 }}>
                    Send via
                </Typography>
                <ToggleButtonGroup
                    data-testid="payment-link-channel"
                    value={channel}
                    exclusive
                    onChange={(_, v) => { if (v) setChannel(v); }}
                    size="small"
                >
                    {CHANNELS.map(({ value, label, icon }) => (
                        <ToggleButton key={value} value={value} sx={{ textTransform: 'none', fontSize: '12px', gap: 0.5, px: 1.25 }}>
                            <Iconify icon={icon} width={13} height={13} />
                            {label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {/* Recipient info */}
            {channel === 'email' && patientEmail && (
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.03)', borderRadius: '8px', p: 1.25 }}>
                    <Typography sx={{ fontSize: '12px', color: '#6e6e73' }}>
                        {patientEmail}
                    </Typography>
                </Box>
            )}
            {channel === 'email' && !patientEmail && (
                <Box>
                    <Typography sx={{ fontSize: '12px', color: '#FF5630' }}>
                        No email on file
                    </Typography>
                    <InlineEmailAddField
                        practiceId={practiceId}
                        leadId={leadId}
                        onSuccess={(email) => setEmailAdded(email)}
                    />
                </Box>
            )}
            {channel !== 'email' && patientPhone && (
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.03)', borderRadius: '8px', p: 1.25 }}>
                    <Typography sx={{ fontSize: '12px', color: '#6e6e73' }}>
                        {patientPhone}
                    </Typography>
                </Box>
            )}

            {/* Actions */}
            <Stack direction="row" spacing={1} sx={{ mt: 'auto' }}>
                <Button
                    data-testid="payment-link-submit"
                    variant="contained"
                    disableElevation
                    onClick={handleSend}
                    disabled={submitting || !amount}
                    startIcon={submitting ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:send" width={14} height={14} />}
                    sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        bgcolor: '#5BB5F5',
                        '&:hover': { bgcolor: '#4aa4e5' },
                        boxShadow: 'none',
                    }}
                >
                    {submitting ? 'Sending…' : 'Send link'}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onClose}
                    disabled={submitting}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
                >
                    Cancel
                </Button>
            </Stack>
        </Box>
    );
}

SendPaymentLinkPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadDetail: PropTypes.object,
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

SendPaymentLinkPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    leadDetail: null,
    onComplete: null,
};
